import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useForm, useFieldArray } from 'react-hook-form';
import { format } from 'date-fns';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { ArrowLeft, Calendar as CalendarIcon, Plus, Trash2, Loader2, Image, MapPin, Upload, X, Clock, Repeat, UserCog, DollarSign, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getImageUrl } from '../../utils/apiUrl';
import { useAuth } from '../../contexts/AuthContext';
import { EventOptionsCards, TicketTypesForm, CouponManager } from '../../components/create-event';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const categories = [
  { id: 'music', name: 'Music' },
  { id: 'comedy', name: 'Comedy' },
  { id: 'sports', name: 'Sports' },
  { id: 'wedding', name: 'Wedding' },
  { id: 'nightlife', name: 'Nightlife' },
  { id: 'festivals', name: 'Festivals' },
  { id: 'awards', name: 'Awards' },
  { id: 'arts', name: 'Arts & Theater' },
  { id: 'food', name: 'Food & Drink' },
  { id: 'business', name: 'Business' },
  { id: 'exhibition', name: 'Exhibition' },
];

const eventTypes = [
  { id: 'free', name: 'Free Event', description: 'No tickets required, open to all' },
  { id: 'paid', name: 'Paid Event', description: 'Tickets with pricing required' },
  { id: 'reservation', name: 'Reservation Only', description: 'RSVP required, limited spots' },
];

const daysOfWeek = [
  { id: 'monday', name: 'Mon' },
  { id: 'tuesday', name: 'Tue' },
  { id: 'wednesday', name: 'Wed' },
  { id: 'thursday', name: 'Thu' },
  { id: 'friday', name: 'Fri' },
  { id: 'saturday', name: 'Sat' },
  { id: 'sunday', name: 'Sun' },
];

const currencies = [
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
];

// Africa → Paystack, North America/Europe → Stripe
const AFRICA_COUNTRIES = [
  'Nigeria', 'nigeria', 'NG',
  'Ghana', 'ghana', 'GH',
  'Kenya', 'kenya', 'KE',
  'South Africa', 'south africa', 'ZA',
  'Egypt', 'egypt', 'EG',
  'Tanzania', 'tanzania', 'TZ',
  'Rwanda', 'rwanda', 'RW',
  'Cameroon', 'cameroon', 'CM',
  'Senegal', 'senegal', 'SN',
  'Ivory Coast', 'ivory coast', 'CI',
];

// Map countries to default currencies
const getDefaultCurrency = (country) => {
  const c = (country || '').trim().toLowerCase();
  if (['nigeria', 'ng'].includes(c)) return 'NGN';
  if (['ghana', 'gh'].includes(c)) return 'GHS';
  if (['kenya', 'ke'].includes(c)) return 'KES';
  if (['south africa', 'za'].includes(c)) return 'ZAR';
  if (['canada', 'ca'].includes(c)) return 'CAD';
  if (['united states', 'usa', 'us', 'united states of america'].includes(c)) return 'USD';
  if (['united kingdom', 'uk', 'gb', 'england', 'scotland', 'wales'].includes(c)) return 'GBP';
  if (['germany', 'france', 'spain', 'italy', 'netherlands', 'belgium', 'austria', 'ireland', 'portugal', 'finland', 'de', 'fr', 'es', 'it', 'nl'].includes(c)) return 'EUR';
  return 'USD';
};

const getGatewayForCountry = (country) => {
  const c = (country || '').trim();
  if (AFRICA_COUNTRIES.some(ac => ac.toLowerCase() === c.toLowerCase())) return 'paystack';
  return 'stripe';
};

const defaultTicketType = {
  name: 'General Admission',
  description: '',
  price: 0,
  quantity: '',
  available: '',
  is_free: true
};

export default function CreateEvent() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditing = !!eventId;
  const isSuperAdmin = user?.admin_role === 'super_admin';
  const [loading, setLoading] = useState(false);
  const [fetchingEvent, setFetchingEvent] = useState(isEditing);
  const [date, setDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState('19:00'); // Default 7:00 PM
  const [endTime, setEndTime] = useState('22:00'); // Default 10:00 PM
  const [gettingLocation, setGettingLocation] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // Custom organizer name (super admin only)
  const [customOrganizerName, setCustomOrganizerName] = useState('');
  // Public-facing organizer display name (any organizer, per-event)
  const [organizerDisplayName, setOrganizerDisplayName] = useState('');
  
  // Recurring event state
  // Recurring event state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringDays, setRecurringDays] = useState([]);
  const [openingTime, setOpeningTime] = useState('09:00');
  const [closingTime, setClosingTime] = useState('22:00');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [requiresRsvp, setRequiresRsvp] = useState(false);
  
  // Service fee state - 13.5% for on-site staff, wristbands, in-person sales
  const [serviceFeeEnabled, setServiceFeeEnabled] = useState(false);  
  // Currency state
  const [currency, setCurrency] = useState('NGN');
  
  // Non-refundable event state
  const [isNonRefundable, setIsNonRefundable] = useState(false);
  
  // Fee absorption state - default true (organizer absorbs fees)
  const [absorbFees, setAbsorbFees] = useState(true);

  // Vendor-name-collection toggle (per-event switch only — the actual name is
  // entered by the buyer on the checkout form, not by the organizer).
  const [collectVendorName, setCollectVendorName] = useState(false);

  // Coupons state
  const [coupons, setCoupons] = useState([]);

  // Soft-deleted ticket types — populated only in edit mode via include_deleted=true
  const [deletedTicketTypes, setDeletedTicketTypes] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  // Referral commission state
  const [commissionPercent, setCommissionPercent] = useState('');
  const [commissionBudget, setCommissionBudget] = useState('');

  // Rejection context (for editing rejected events)
  const [rejectionReason, setRejectionReason] = useState(null);
  const [isRejectedEvent, setIsRejectedEvent] = useState(false);
  const { register, handleSubmit, control, watch, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      title: '',
      description: '',
      category: 'music',
      event_type: 'paid',
      venue: '',
      address: '',
      city: 'Lagos',
      country: 'Nigeria',
      latitude: '',
      longitude: '',
      image_url: '',
      is_published: false,
      max_reservations: '',
      free_ticket_limit: '',
      ticket_types: [{ ...defaultTicketType }]
    }
  });

  // IANA timezone captured silently from the editor's browser at mount, overridden by the
  // event's saved timezone during edit-mode prefill. Using a ref keeps this out of state
  // (no re-renders, no UI), per spec. Sent in both POST and PUT payloads.
  const tzRef = useRef(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ticket_types'
  });

  // Watch image_url for preview
  const watchedImageUrl = watch('image_url');
  
  // Watch country for service fee eligibility (only Canada and Nigeria)
  const watchedCountry = watch('country');
  const isServiceFeeEligible = ['Nigeria', 'Canada', 'nigeria', 'canada', 'NG', 'CA'].includes(watchedCountry?.trim());
  
  // Reset service fee if country becomes ineligible
  useEffect(() => {
    if (!isServiceFeeEligible && serviceFeeEnabled) {
      setServiceFeeEnabled(false);
    }
  }, [isServiceFeeEligible, serviceFeeEnabled]);

  // Auto-select currency when country changes
  useEffect(() => {
    if (watchedCountry && !isEditing) {
      setCurrency(getDefaultCurrency(watchedCountry));
    }
  }, [watchedCountry, isEditing]);
  
  // Handle image file upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload file
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        
      });
      
      setValue('image_url', response.data.url);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  // Clear image
  const clearImage = () => {
    setValue('image_url', '');
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Get current location
  const getCurrentLocation = () => {
    if (!('geolocation' in navigator)) {
      toast.error('Geolocation not supported');
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setValue('latitude', position.coords.latitude.toFixed(6));
        setValue('longitude', position.coords.longitude.toFixed(6));
        toast.success('Location captured!');
        setGettingLocation(false);
      },
      (error) => {
        toast.error('Could not get location: ' + error.message);
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Fetch event data if editing.
  // Note: explicitly attaches the JWT from localStorage to defeat a cold-reload race —
  // AuthContext's effect that sets axios defaults can run AFTER this effect, leaving the
  // request unauthenticated and the backend stripping our soft-deleted ticket types.
  useEffect(() => {
    if (isEditing) {
      const fetchEvent = async () => {
        try {
          const token = localStorage.getItem('token');
          const config = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
          // include_deleted=true is honored only when the requester is the event's organizer.
          // Public callers / other organizers get a stripped response automatically.
          const response = await axios.get(`${API_URL}/api/events/${eventId}?include_deleted=true`, config);
          const event = response.data;

          const activeTypes = (event.ticket_types || []).filter(tt => !tt.is_deleted);
          const deletedTypes = (event.ticket_types || []).filter(tt => tt.is_deleted);
          setDeletedTicketTypes(deletedTypes);

          reset({
            title: event.title,
            description: event.description,
            category: event.category,
            event_type: event.event_type || 'paid',
            venue: event.venue,
            address: event.address,
            city: event.city,
            country: event.country,
            latitude: event.latitude || '',
            longitude: event.longitude || '',
            image_url: event.image_url || '',
            is_published: event.is_published,
            max_reservations: event.max_reservations || '',
            free_ticket_limit: event.free_ticket_limit || '',
            ticket_types: activeTypes.map(tt => ({
              ...tt,
              is_free: tt.price === 0
            }))
          });
          
          // Set image preview if URL exists
          if (event.image_url) {
            setImagePreview(getImageUrl(event.image_url));
          }
          
          // Extract date and time from event - only if date is valid
          if (event.date) {
            const eventDateTime = new Date(event.date);
            if (!isNaN(eventDateTime.getTime())) {
              setDate(eventDateTime);
              setStartTime(format(eventDateTime, 'HH:mm'));
            }
          }
          
          if (event.end_date) {
            const endDateTime = new Date(event.end_date);
            if (!isNaN(endDateTime.getTime())) {
              setEndDate(endDateTime);
              setEndTime(format(endDateTime, 'HH:mm'));
            }
          }
          
          // Load recurring event data
          if (event.is_recurring) {
            setIsRecurring(true);
            if (event.recurring_schedule) {
              setRecurringDays(event.recurring_schedule.days || []);
              setOpeningTime(event.recurring_schedule.opening_time || '09:00');
              setClosingTime(event.recurring_schedule.closing_time || '22:00');
            }
            if (event.price_range) {
              setMinPrice(event.price_range.min_price?.toString() || '');
              setMaxPrice(event.price_range.max_price?.toString() || '');
            }
            setRequiresRsvp(event.requires_rsvp || false);
          }
          
          // Load service fee setting
          setServiceFeeEnabled(event.service_fee_enabled || false);
          
          // Load non-refundable setting
          setIsNonRefundable(event.is_non_refundable || false);
          
          // Load fee absorption setting (default to true if not set)
          setAbsorbFees(event.absorb_fees !== false);

          // Load vendor-name-collection toggle. The actual name is buyer-
          // entered per checkout, so no vendor_name string to hydrate here.
          setCollectVendorName(event.collect_vendor_name === true);
          
          // Load custom organizer name if editing (for super admin)
          if (event.organizer_name) {
            setCustomOrganizerName(event.organizer_name);
          }
          // Load public organizer display name (always editable, all organizers)
          setOrganizerDisplayName(event.organizer_display_name || '');
          
          // Load currency
          if (event.currency) {
            setCurrency(event.currency);
          }

          // Timezone: prefer the value saved on the event, fall back to the editor's
          // browser timezone so legacy events (timezone=null) get a sensible default
          // instead of being persisted back as null on the next save.
          tzRef.current = event.timezone || tzRef.current;
          
          // Load referral commission settings — use explicit null check so 0 is preserved
          if (event.referral_commission_percent !== null && event.referral_commission_percent !== undefined) {
            setCommissionPercent(String(event.referral_commission_percent));
          }
          if (event.referral_commission_budget !== null && event.referral_commission_budget !== undefined) {
            setCommissionBudget(String(event.referral_commission_budget));
          }
          
          // Load existing coupons
          try {
            const couponsRes = await axios.get(`${API_URL}/api/coupons/event/${eventId}`);
            setCoupons(couponsRes.data || []);
          } catch {
            // No coupons or not authorized
          }

          // Track rejection state for resubmission flow
          if (event.approval_status === 'rejected') {
            setIsRejectedEvent(true);
            setRejectionReason(event.rejection_reason || 'No reason provided');
          }
        } catch (error) {
          console.error('Error fetching event:', error);
          toast.error('Failed to load event');
          navigate('/dashboard');
        } finally {
          setFetchingEvent(false);
        }
      };
      fetchEvent();
    }
  }, [eventId, isEditing, navigate, reset]);

  const onSubmit = async (data) => {
    // Date is required only for non-recurring events
    if (!isRecurring && !date) {
      toast.error('Please select an event date');
      return;
    }

    setLoading(true);
    try {
      // Process ticket types
      const ticketTypes = [];
      for (let index = 0; index < data.ticket_types.length; index++) {
        const tt = data.ticket_types[index];
        const rawQty = tt.quantity === '' || tt.quantity === null || tt.quantity === undefined ? null : parseInt(tt.quantity);
        // Reject only if they typed something invalid (not blank)
        if (rawQty !== null && (isNaN(rawQty) || rawQty < 1)) {
          toast.error(`Please set a valid quantity for ticket "${tt.name}"`);
          setLoading(false);
          return;
        }
        const quantity = rawQty; // null = unlimited
        const available = isEditing 
          ? (tt.available != null && tt.available !== '' ? parseInt(tt.available) : quantity)
          : quantity;
        
        ticketTypes.push({
          ticket_id: tt.ticket_id || `tkt_${Date.now()}_${index}`,
          name: tt.name,
          description: tt.description,
          price: tt.is_free ? 0 : parseFloat(tt.price) || 0,
          quantity: quantity,
          available: available,
          is_free: tt.is_free
        });
      }

      // Combine date with time
      const combineDateAndTime = (dateObj, timeStr) => {
        if (!dateObj || isNaN(dateObj.getTime())) return null;
        const [hours, minutes] = timeStr.split(':').map(Number);
        const combined = new Date(dateObj);
        combined.setHours(hours, minutes, 0, 0);
        return combined;
      };

      const eventDateTime = date && !isNaN(date.getTime()) ? combineDateAndTime(date, startTime) : null;
      const eventEndDateTime = endDate && !isNaN(endDate.getTime()) ? combineDateAndTime(endDate, endTime) : null;

      const eventData = {
        title: data.title,
        description: data.description,
        category: data.category,
        event_type: data.event_type,
        date: eventDateTime ? eventDateTime.toISOString() : null,  // Optional for recurring events
        end_date: eventEndDateTime ? eventEndDateTime.toISOString() : null,
        venue: data.venue,
        address: data.address,
        city: data.city,
        country: data.country,
        latitude: data.latitude ? parseFloat(data.latitude) : null,
        longitude: data.longitude ? parseFloat(data.longitude) : null,
        image_url: data.image_url || null,
        is_published: data.is_published,
        max_reservations: data.event_type === 'reservation' && data.max_reservations ? parseInt(data.max_reservations) : null,
        ticket_types: ticketTypes,
        // Recurring event fields
        is_recurring: isRecurring,
        recurring_schedule: isRecurring ? {
          days: recurringDays,
          opening_time: openingTime,
          closing_time: closingTime
        } : null,
        price_range: isRecurring && (minPrice || maxPrice) ? {
          min_price: parseFloat(minPrice) || 0,
          max_price: parseFloat(maxPrice) || 0,
          currency: 'NGN'
        } : null,
        requires_rsvp: requiresRsvp,
        // Service fee: 13.5% for on-site staff, wristbands, in-person ticket sales
        service_fee_enabled: serviceFeeEnabled,
        // Non-refundable event: users cannot request refunds
        is_non_refundable: isNonRefundable,
        // Fee absorption: true = organizer absorbs, false = users pay
        absorb_fees: absorbFees,
        // Vendor-name-collection toggle (buyer enters the actual name at
        // checkout — organizer never types it).
        collect_vendor_name: collectVendorName,
        // Custom organizer name (super admin only)
        custom_organizer_name: isSuperAdmin && customOrganizerName.trim() ? customOrganizerName.trim() : null,
        // Public-facing organizer display name (blank -> null = use account name)
        organizer_display_name: organizerDisplayName.trim() ? organizerDisplayName.trim() : null,
        // Free ticket limit (optional) - for free events
        free_ticket_limit: data.event_type === 'free' && data.free_ticket_limit ? parseInt(data.free_ticket_limit) : null,
        // Max tickets per user (optional) - applies to all event types
        max_tickets_per_user: data.max_tickets_per_user ? parseInt(data.max_tickets_per_user) : null,
        // Currency
        currency: currency,
        // Referral commission settings
        referral_commission_percent: commissionPercent ? parseFloat(commissionPercent) : null,
        referral_commission_budget: commissionBudget ? parseFloat(commissionBudget) : null
      };

      if (isEditing) {
        await axios.put(`${API_URL}/api/events/${eventId}`, eventData);
        // Save coupons for existing event
        if (coupons.length > 0) {
          const existingCoupons = await axios.get(`${API_URL}/api/coupons/event/${eventId}`).then(r => r.data).catch(() => []);
          const existingCodes = existingCoupons.map(c => c.code);
          for (const coupon of coupons) {
            if (!existingCodes.includes(coupon.code)) {
              await axios.post(`${API_URL}/api/coupons`, { ...coupon, event_id: eventId }).catch(() => {});
            }
          }
        }
        toast.success(isRejectedEvent ? 'Event resubmitted for review!' : 'Event updated successfully!');
      } else {
        const response = await axios.post(`${API_URL}/api/events`, eventData);
        const newEventId = response.data?.event_id;
        // Save coupons for new event
        if (newEventId && coupons.length > 0) {
          for (const coupon of coupons) {
            await axios.post(`${API_URL}/api/coupons`, { ...coupon, event_id: newEventId }).catch(() => {});
          }
        }
        toast.success('Event created successfully!');
      }
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  const watchTicketTypes = watch('ticket_types');

  // Restore a soft-deleted ticket type via PATCH /api/ticket-types/{id}/restore.
  // Moves the type back to the active section and re-publishes it for purchase.
  const handleRestoreTicketType = async (ticketId) => {
    if (!ticketId) return;
    setRestoringId(ticketId);
    try {
      const res = await axios.patch(`${API_URL}/api/ticket-types/${ticketId}/restore`);
      const restored = res.data.ticket_type;
      setDeletedTicketTypes(prev => prev.filter(tt => tt.ticket_id !== ticketId));
      // Append to the active form list so it can be edited/saved with the event.
      append({ ...restored, is_free: restored.price === 0 });
      toast.success('Ticket type restored');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore ticket type');
    } finally {
      setRestoringId(null);
    }
  };

  if (fetchingEvent) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="space-y-6">
            <div className="h-8 w-48 skeleton rounded" />
            <div className="h-96 skeleton rounded-xl" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <h1 className="text-3xl font-bold mb-8">{isEditing ? (isRejectedEvent ? 'Edit & Resubmit Event' : 'Edit Event') : 'Create New Event'}</h1>

        {isRejectedEvent && rejectionReason && (
          <div className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/20" data-testid="rejection-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-destructive text-sm">This event was rejected</p>
                <p className="text-sm text-destructive/80 mt-1">{rejectionReason}</p>
                <p className="text-xs text-muted-foreground mt-2">Make the necessary changes and resubmit for review.</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Event Details</CardTitle>
              <CardDescription>Basic information about your event</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Event Title *</Label>
                <Input
                  id="title"
                  {...register('title', { required: 'Title is required' })}
                  placeholder="e.g., Summer Music Festival 2026"
                  data-testid="event-title-input"
                />
                {errors.title && <p className="text-sm text-destructive">{errors.title.message}</p>}
              </div>

              {/* Public organizer display name — available to all organizers, always editable */}
              <div className="space-y-2">
                <Label htmlFor="organizer_display_name">Organizer name (shown publicly)</Label>
                <Input
                  id="organizer_display_name"
                  value={organizerDisplayName}
                  onChange={(e) => setOrganizerDisplayName(e.target.value)}
                  placeholder={user?.name || 'Your account name'}
                  maxLength={60}
                  data-testid="organizer-display-name-input"
                />
                <p className="text-xs text-muted-foreground">Leave blank to use your account name.</p>
              </div>

              {/* Custom Organizer Name - Super Admin Only */}
              {isSuperAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="custom_organizer_name" className="flex items-center gap-2">
                    <UserCog className="w-4 h-4 text-primary" />
                    Organized By (Custom)
                  </Label>
                  <Input
                    id="custom_organizer_name"
                    value={customOrganizerName}
                    onChange={(e) => setCustomOrganizerName(e.target.value)}
                    placeholder="Leave empty to use your name, or enter custom organizer name"
                    data-testid="custom-organizer-name-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    As a Super Admin, you can customize the &quot;Organized by&quot; display name for this event.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  {...register('description', { required: 'Description is required' })}
                  placeholder="Tell attendees what to expect at your event..."
                  rows={5}
                  data-testid="event-description-input"
                />
                {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={watch('category')}
                    onValueChange={(value) => setValue('category', value)}
                  >
                    <SelectTrigger data-testid="event-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Event Date - Hide completely for recurring events */}
                {!isRecurring && (
                  <div className="space-y-2">
                    <Label>Event Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !date && "text-muted-foreground"
                          )}
                          data-testid="event-date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          initialFocus
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>

              {/* Time Selection - Hide for recurring events since they have their own hours */}
              {!isRecurring && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startTime">Start Time *</Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="startTime"
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="pl-10"
                        data-testid="event-start-time"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="endTime">End Time (Optional)</Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="endTime"
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="pl-10"
                        data-testid="event-end-time"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Set end time if your event spans to a different time
                    </p>
                  </div>
                </div>
              )}

              {/* End Date for Exhibition and Festival Events - Optional */}
              {!isRecurring && (watch('category') === 'exhibition' || watch('category') === 'festivals') && (
                <div className="space-y-2">
                  <Label>Event End Date (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endDate && "text-muted-foreground"
                        )}
                        data-testid="event-end-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "PPP") : "Pick an end date (optional)"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        initialFocus
                        disabled={(d) => d < (date || new Date())}
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">
                    {watch('category') === 'festivals' 
                      ? "For festivals that span multiple days, set an end date"
                      : "For exhibitions that span multiple days, set an end date"
                    }
                  </p>
                </div>
              )}

              {/* Event Type Selection */}
              <div className="space-y-3">
                <Label>Event Type *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {eventTypes.map((type) => (
                    <div
                      key={type.id}
                      className={cn(
                        "relative p-4 border-2 rounded-xl cursor-pointer transition-all hover:border-primary/50",
                        watch('event_type') === type.id
                          ? "border-primary bg-primary/5"
                          : "border-border"
                      )}
                      onClick={() => setValue('event_type', type.id)}
                      data-testid={`event-type-${type.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center",
                          watch('event_type') === type.id
                            ? "border-primary bg-primary"
                            : "border-muted-foreground"
                        )}>
                          {watch('event_type') === type.id && (
                            <div className="w-1.5 h-1.5 rounded-full bg-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{type.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Max Reservations - Only show for Reservation type */}
              {watch('event_type') === 'reservation' && (
                <div className="space-y-2 p-4 rounded-xl bg-muted/50">
                  <Label htmlFor="max_reservations">Maximum Reservations</Label>
                  <Input
                    id="max_reservations"
                    type="number"
                    {...register('max_reservations')}
                    placeholder="e.g., 100 (leave empty for unlimited)"
                    data-testid="max-reservations-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Set a limit on how many people can RSVP. Leave empty for unlimited.
                  </p>
                </div>
              )}

              {/* Recurring Event Toggle */}
              <div className="space-y-4 p-4 rounded-xl border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Recurring Event</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Enable for events that happen regularly (e.g., weekly club nights, daily shows)
                    </p>
                  </div>
                  <Switch
                    checked={isRecurring}
                    onCheckedChange={setIsRecurring}
                    data-testid="recurring-event-toggle"
                  />
                </div>

                {/* Recurring Event Options */}
                {isRecurring && (
                  <div className="space-y-4 pt-4 border-t border-border">
                    {/* Days of Week */}
                    <div className="space-y-2">
                      <Label>Operating Days</Label>
                      <div className="flex flex-wrap gap-2">
                        {daysOfWeek.map((day) => (
                          <Button
                            key={day.id}
                            type="button"
                            variant={recurringDays.includes(day.id) ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              if (recurringDays.includes(day.id)) {
                                setRecurringDays(recurringDays.filter(d => d !== day.id));
                              } else {
                                setRecurringDays([...recurringDays, day.id]);
                              }
                            }}
                            className="min-w-[50px]"
                            data-testid={`day-${day.id}`}
                          >
                            {day.name}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Opening & Closing Hours */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="openingTime">Opening Time</Label>
                        <div className="relative">
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="openingTime"
                            type="time"
                            value={openingTime}
                            onChange={(e) => setOpeningTime(e.target.value)}
                            className="pl-10"
                            data-testid="opening-time"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="closingTime">Closing Time</Label>
                        <div className="relative">
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="closingTime"
                            type="time"
                            value={closingTime}
                            onChange={(e) => setClosingTime(e.target.value)}
                            className="pl-10"
                            data-testid="closing-time"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Price Range */}
                    <div className="space-y-2">
                      <Label>Price Range (Optional)</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{currencies.find(c => c.code === currency)?.symbol || '$'}</span>
                          <Input
                            type="number"
                            placeholder="Min price"
                            value={minPrice}
                            onChange={(e) => setMinPrice(e.target.value)}
                            className="pl-8"
                            data-testid="min-price"
                          />
                        </div>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{currencies.find(c => c.code === currency)?.symbol || '$'}</span>
                          <Input
                            type="number"
                            placeholder="Max price"
                            value={maxPrice}
                            onChange={(e) => setMaxPrice(e.target.value)}
                            className="pl-8"
                            data-testid="max-price"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Set a price range for entry
                      </p>
                    </div>

                    {/* Requires RSVP */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div>
                        <Label>Require RSVP</Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Attendees must reserve a spot before attending
                        </p>
                      </div>
                      <Switch
                        checked={requiresRsvp}
                        onCheckedChange={setRequiresRsvp}
                        data-testid="requires-rsvp-toggle"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Event Image Upload Section */}
              <div className="space-y-3">
                <Label>Event Image</Label>

                <div className="space-y-3">
                  {/* Upload Area */}
                    <div
                      className={cn(
                        "border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer hover:border-primary/50",
                        uploadingImage && "opacity-50 pointer-events-none",
                        (imagePreview || watchedImageUrl) ? "border-primary/30 bg-primary/5" : "border-border"
                      )}
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="image-upload-dropzone"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                        data-testid="image-file-input"
                      />
                      
                      {uploadingImage ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-8 h-8 animate-spin text-primary" />
                          <p className="text-sm text-muted-foreground">Uploading...</p>
                        </div>
                      ) : (imagePreview || watchedImageUrl) ? (
                        <div className="relative">
                          <img
                            src={imagePreview || watchedImageUrl}
                            alt="Event preview"
                            className="max-h-48 mx-auto rounded-lg object-cover"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearImage();
                            }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                            <Upload className="w-6 h-6 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Click to upload</p>
                            <p className="text-xs text-muted-foreground">JPG, PNG, GIF or WebP (max 5MB)</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
              </div>
            </CardContent>
          </Card>

          {/* Venue Info */}
          <Card>
            <CardHeader>
              <CardTitle>Venue Details</CardTitle>
              <CardDescription>Where is your event taking place?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="venue">Venue Name *</Label>
                <Input
                  id="venue"
                  {...register('venue', { required: 'Venue is required' })}
                  placeholder="e.g., Eko Convention Centre"
                  data-testid="event-venue-input"
                />
                {errors.venue && <p className="text-sm text-destructive">{errors.venue.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  {...register('address', { required: 'Address is required' })}
                  placeholder="e.g., 123 Victoria Island"
                  data-testid="event-address-input"
                />
                {errors.address && <p className="text-sm text-destructive">{errors.address.message}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    {...register('city', { required: 'City is required' })}
                    placeholder="e.g., Lagos"
                    data-testid="event-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    {...register('country')}
                    placeholder="e.g., Nigeria"
                    data-testid="event-country-input"
                  />
                </div>
              </div>

              {/* Currency & Gateway */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Currency *</Label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger data-testid="event-currency-select">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.symbol} {c.code} — {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Payment Gateway</Label>
                  <div className={cn(
                    "h-10 px-3 flex items-center gap-2 rounded-md border border-border bg-muted/50 text-sm",
                  )}>
                    {getGatewayForCountry(watchedCountry) === 'stripe' ? (
                      <>
                        <span className="font-semibold text-[#635bff]">Stripe</span>
                        <span className="text-muted-foreground">— North America / Europe</span>
                      </>
                    ) : (
                      <>
                        <span className="font-semibold text-[#00c3f7]">Paystack</span>
                        <span className="text-muted-foreground">— Africa</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Automatically assigned based on event country
                  </p>
                </div>
              </div>

              {/* Location Coordinates for "Near Me" feature */}
              <div className="p-4 rounded-xl bg-muted/50 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">Event Location</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Add coordinates so users can find your event in &quot;Events Near Me&quot;
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={getCurrentLocation}
                    disabled={gettingLocation}
                    data-testid="get-location-btn"
                  >
                    {gettingLocation ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <MapPin className="w-4 h-4 mr-2" />
                    )}
                    {gettingLocation ? 'Getting...' : 'Use Current Location'}
                  </Button>
                </div>
                {/* Hidden fields for latitude and longitude - auto-populated by location services */}
                <input type="hidden" {...register('latitude')} />
                <input type="hidden" {...register('longitude')} />
              </div>
            </CardContent>
          </Card>

          {/* Ticket Types - Only show for paid non-recurring events */}
          {watch('event_type') === 'paid' && !isRecurring && (
            <Card>
              <CardHeader>
                <CardTitle>Ticket Types</CardTitle>
                <CardDescription>Create different ticket tiers for your event</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {fields.map((field, index) => (
                  <div key={field.id} className="p-4 border rounded-xl space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Ticket Type {index + 1}</h4>
                      {fields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => remove(index)}
                          className="text-destructive"
                          data-testid={`remove-ticket-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Ticket Name *</Label>
                        <Input
                          {...register(`ticket_types.${index}.name`, { required: true })}
                          placeholder="e.g., VIP, Regular, Early Bird"
                          data-testid={`ticket-name-${index}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Quantity <span className="text-muted-foreground font-normal">(leave blank for unlimited)</span></Label>
                        <Input
                          type="number"
                          {...register(`ticket_types.${index}.quantity`, { min: 1 })}
                          placeholder="Unlimited"
                          data-testid={`ticket-quantity-${index}`}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        {...register(`ticket_types.${index}.description`)}
                        placeholder="What's included with this ticket?"
                        data-testid={`ticket-description-${index}`}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={watchTicketTypes?.[index]?.is_free}
                          onCheckedChange={(checked) => {
                            setValue(`ticket_types.${index}.is_free`, checked);
                            if (checked) setValue(`ticket_types.${index}.price`, 0);
                          }}
                          data-testid={`ticket-free-switch-${index}`}
                        />
                        <Label>Free Ticket</Label>
                      </div>
                      
                      {!watchTicketTypes?.[index]?.is_free && (
                        <div className="flex items-center gap-2">
                          <Label>Price ({currencies.find(c => c.code === currency)?.symbol || '$'})</Label>
                          <Input
                            type="number"
                            {...register(`ticket_types.${index}.price`, { min: 0 })}
                            placeholder="0"
                            className="w-32"
                            data-testid={`ticket-price-${index}`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => append({ ...defaultTicketType })}
                  className="w-full"
                  data-testid="add-ticket-type-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Ticket Type
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Deleted Ticket Types — only rendered in edit mode when at least one exists */}
          {isEditing && deletedTicketTypes.length > 0 && (
            <Card data-testid="deleted-ticket-types-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle>Deleted Ticket Types</CardTitle>
                    <CardDescription>
                      Soft-deleted because they have sales. Sales figures are preserved.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="show-deleted-toggle" className="text-sm text-muted-foreground">
                      Show deleted
                    </Label>
                    <Switch
                      id="show-deleted-toggle"
                      checked={showDeleted}
                      onCheckedChange={setShowDeleted}
                      data-testid="show-deleted-toggle"
                    />
                  </div>
                </div>
              </CardHeader>
              {showDeleted && (
                <CardContent className="space-y-3" data-testid="deleted-ticket-types-list">
                  {deletedTicketTypes.map((tt) => (
                    <div
                      key={tt.ticket_id}
                      className="flex items-center justify-between p-4 rounded-xl border border-destructive/30 bg-destructive/5"
                      data-testid={`deleted-ticket-row-${tt.ticket_id}`}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{tt.name}</p>
                          <span
                            className="text-xs font-medium px-2 py-0.5 rounded-full bg-destructive/10 text-destructive border border-destructive/40"
                            data-testid="deleted-badge"
                          >
                            Deleted
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          ₦{Number(tt.price || 0).toLocaleString()} •{' '}
                          <span data-testid={`deleted-sold-${tt.ticket_id}`}>{tt.sold ?? 0} sold</span>{' '}
                          • Revenue ₦{Number(tt.revenue || 0).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={restoringId === tt.ticket_id}
                        onClick={() => handleRestoreTicketType(tt.ticket_id)}
                        data-testid={`restore-ticket-btn-${tt.ticket_id}`}
                      >
                        {restoringId === tt.ticket_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Restore'
                        )}
                      </Button>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          )}

          {/* Info card for Free events */}
          {watch('event_type') === 'free' && (
            <Card className="border-border bg-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                    <span className="text-lg">🎉</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium">Free Event</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      This event is open to everyone! Attendees can claim free tickets.
                    </p>
                    
                    {/* Optional Free Ticket Limit */}
                    <div className="mt-4 pt-4 border-t border-border">
                      <Label htmlFor="free_ticket_limit" className="text-sm">
                        Free Ticket Limit (Optional)
                      </Label>
                      <div className="flex items-center gap-3 mt-2">
                        <Input
                          id="free_ticket_limit"
                          type="number"
                          min="1"
                          placeholder="Unlimited"
                          {...register('free_ticket_limit')}
                          className="max-w-[150px]"
                          data-testid="free-ticket-limit-input"
                        />
                        <span className="text-sm text-muted-foreground">tickets</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Leave empty for unlimited free tickets, or set a limit for capacity control.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info card for Reservation events */}
          {watch('event_type') === 'reservation' && (
            <Card className="border-border bg-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                    <span className="text-lg">📋</span>
                  </div>
                  <div>
                    <h3 className="font-medium">Reservation Only Event</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Attendees must RSVP to secure their spot. No payment required, but registration is mandatory.
                      {watch('max_reservations') && ` Limited to ${watch('max_reservations')} spots.`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Max Tickets Per User — applies to all event types (anti-scalping) */}
          {(watch('event_type') === 'paid' || watch('event_type') === 'free') && (
            <Card className="border-border bg-card">
              <CardContent className="p-6">
                <Label htmlFor="max_tickets_per_user" className="text-sm font-medium">
                  Maximum Tickets Per Person (Optional)
                </Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    id="max_tickets_per_user"
                    type="number"
                    min="1"
                    placeholder="e.g. 4"
                    {...register('max_tickets_per_user')}
                    className="max-w-[150px]"
                    data-testid="max-tickets-per-user-input"
                  />
                  <span className="text-sm text-muted-foreground">per person</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Limit how many tickets each user can purchase. Prevents bulk buying by scalpers. Leave empty for no limit.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Event Options - Service Fee, Non-Refundable, Fee Absorption, Vendor Name Toggle */}
          <EventOptionsCards
            serviceFeeEnabled={serviceFeeEnabled}
            setServiceFeeEnabled={setServiceFeeEnabled}
            isServiceFeeEligible={isServiceFeeEligible}
            isNonRefundable={isNonRefundable}
            setIsNonRefundable={setIsNonRefundable}
            absorbFees={absorbFees}
            setAbsorbFees={setAbsorbFees}
            collectVendorName={collectVendorName}
            setCollectVendorName={setCollectVendorName}
          />

          {/* Coupons */}
          <CouponManager
            coupons={coupons}
            setCoupons={setCoupons}
            currencySymbol={(currencies.find(c => c.code === currency) || currencies[0]).symbol}
          />

          {/* Referral Commission Settings */}
          <Card>
            <CardContent className="p-6">
              <div className="mb-4">
                <h3 className="font-medium">Affiliate Referral Commission</h3>
                <p className="text-sm text-muted-foreground">
                  Set a commission rate for affiliates who drive ticket sales via referral links. Commission is deducted from your payout.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Commission Rate (%)</label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    step="0.5"
                    placeholder="e.g. 5"
                    value={commissionPercent}
                    onChange={(e) => setCommissionPercent(e.target.value)}
                    data-testid="commission-percent-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Leave empty to disable referral commissions</p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Max Commission Budget ({(currencies.find(c => c.code === currency) || currencies[0]).symbol})
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="100"
                    placeholder="e.g. 50000"
                    value={commissionBudget}
                    onChange={(e) => setCommissionBudget(e.target.value)}
                    data-testid="commission-budget-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Optional cap on total commissions paid out</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Publish Settings */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Publish Event</h3>
                  <p className="text-sm text-muted-foreground">
                    Make your event visible to the public
                  </p>
                </div>
                <Switch
                  checked={watch('is_published')}
                  onCheckedChange={(checked) => setValue('is_published', checked)}
                  data-testid="publish-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 btn-primary"
              disabled={loading}
              data-testid="submit-event-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditing ? (isRejectedEvent ? 'Resubmitting...' : 'Updating...') : 'Creating...'}
                </>
              ) : (
                isEditing ? (isRejectedEvent ? 'Resubmit for Review' : 'Update Event') : 'Create Event'
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
